// Force re-build 2
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Row, Col, Button, Spinner } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Formik,
  Form as FormikForm,
  useFormikContext,
} from "formik";
import * as Yup from "yup";

// Import common components
import TextInput from "../../../common/FormFields/TextInput.jsx";
import CustomAsyncDropdown from "../../../common/CustomAsyncDropdown.jsx";

// Import services
import {
  getOrganizations,
  getOrganizationById,
  updateOrganization,
} from "../../../../../services/organizationServices.js";
import { updateContact } from "../../../../../services/contactServices.js";
import { 
  getPlacementOfficers 
} from "../../../../../services/PlacementOfficerServices.js";

const PAGE_LIMIT = 20;

// ─── ScrollToFieldError ───────────────────────────────────────────────────────
const ScrollToFieldError = () => {
  const { submitCount, isValid, errors } = useFormikContext();
  useEffect(() => {
    if (isValid || submitCount === 0) return;
    const fieldErrorNames = Object.keys(errors);
    if (!fieldErrorNames.length) return;
    setTimeout(() => {
      const el =
        document.querySelector(`input[name='${fieldErrorNames[0]}']`) ||
        document.querySelector(`textarea[name='${fieldErrorNames[0]}']`) ||
        document.querySelector(`[data-field='${fieldErrorNames[0]}']`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  }, [submitCount, isValid, errors]);
  return null;
};

// Helper to robustly parse IDs from strings, arrays, or objects
const parseIds = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) {
    return field.map(item => typeof item === 'object' ? item.id : String(item)).filter(Boolean);
  }
  if (typeof field === 'string') {
    return field.split(',').map(s => s.trim()).filter(Boolean);
  }
  if (typeof field === 'object' && field !== null) {
    return field.id ? [field.id] : [];
  }
  return [];
};

const ContactDetailsTab = (props) => {
  const {
    contact,
    onHide,
    onUpdateContact,
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const isComponentMounted = useRef(true);
  
  // ── Dropdown state ─────────────────────────────────────────────────────────
  const [officersOptions, setOfficersOptions] = useState([]);
  const [officersPage, setOfficersPage] = useState(1);
  const [officersHasMore, setOfficersHasMore] = useState(true);
  const [officersLoading, setOfficersLoading] = useState(false);
  const [officersSearch, setOfficersSearch] = useState("");

  const [orgsOptions, setOrgsOptions] = useState([]);
  const [orgsPage, setOrgsPage] = useState(1);
  const [orgsHasMore, setOrgsHasMore] = useState(true);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgsSearch, setOrgsSearch] = useState("");
  const [hasFetchedOfficers, setHasFetchedOfficers] = useState(false);
  const [hasFetchedOrgs, setHasFetchedOrgs] = useState(false);

  const orgsCache = useRef(new Map());
  const officersCache = useRef(new Map());
  const abortControllers = useRef({
    officers: null,
    orgs: null,
  });

  useEffect(() => {
    isComponentMounted.current = true;
    return () => {
      isComponentMounted.current = false;
      Object.keys(abortControllers.current).forEach(key => {
        if (abortControllers.current[key]) {
          abortControllers.current[key].abort();
        }
      });
    };
  }, []);

  // ── Fetch Functions ────────────────────────────────────────────────────────
  
  const fetchOfficers = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    if (officersLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.officers) abortControllers.current.officers.abort();
    const abortController = new AbortController();
    abortControllers.current.officers = abortController;
    
    setOfficersLoading(true);
    try {
      const response = await getPlacementOfficers(PAGE_LIMIT, page, search, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      
      const mapped = (response?.data || []).map((o) => {
        const name = o.firstName || o.lastName 
          ? `${o.firstName || ""} ${o.lastName || ""}`.trim() 
          : (o.name || "Unknown");
        return {
          value: o.id,
          label: name,
        };
      });
      
      setOfficersOptions((prev) => append ? [...prev, ...mapped] : mapped);
      setOfficersPage(page);
      setOfficersHasMore(page < (response?.pagination?.totalPages || 1));
      setHasFetchedOfficers(true);
    } catch (e) {
      if (e.name !== 'AbortError') console.error("Failed to load officers:", e);
    } finally {
      if (isComponentMounted.current) setOfficersLoading(false);
    }
  }, [officersLoading]);

  const fetchOrgs = useCallback(async ({ page = 1, search = "", append = false } = {}) => {
    if (orgsLoading || !isComponentMounted.current) return;
    
    if (abortControllers.current.orgs) abortControllers.current.orgs.abort();
    const abortController = new AbortController();
    abortControllers.current.orgs = abortController;
    
    setOrgsLoading(true);
    try {
      const response = await getOrganizations(PAGE_LIMIT, page, search ? { search } : {}, { signal: abortController.signal });
      if (!isComponentMounted.current) return;
      
      const rawData = response?.data?.data || response?.data || [];
      // Cache the full objects for later use in submit
      rawData.forEach(org => { if (org.id) orgsCache.current.set(org.id, org); });

      const mapped = rawData.map((org) => ({
        value: org.id,
        label: org.name || "Unnamed Organization",
      }));
      
      setOrgsOptions((prev) => append ? [...prev, ...mapped] : mapped);
      setOrgsPage(page);
      setOrgsHasMore(mapped.length === PAGE_LIMIT);
    } catch (e) {
      if (e.name !== 'AbortError') console.error("Failed to load organizations:", e);
    } finally {
      if (isComponentMounted.current) setOrgsLoading(false);
    }
  }, [orgsLoading]);

  const hasInitialLoaded = useRef(false);

  // Sync with parent data if it arrives late
  useEffect(() => {
    if (props.organizations?.length > 0) {
      const mapped = props.organizations.map(org => ({ value: org.id, label: org.name || "Unnamed Organization" }));
      setOrgsOptions(mapped);
    }
  }, [props.organizations]);

  useEffect(() => {
    if (props.users?.length > 0) {
      const mapped = props.users.map(u => {
        const name = u.firstName || u.lastName 
          ? `${u.firstName || ""} ${u.lastName || ""}`.trim() 
          : (u.name || "Unknown");
        return {
          value: u.id,
          label: name,
        };
      });
      setOfficersOptions(mapped);
    }
  }, [props.users]);

  // Initial Load
  useEffect(() => {
    if (!contact?.id || hasInitialLoaded.current) return;
    
    const initializeData = async () => {
      hasInitialLoaded.current = true;
      // Pre-population is already handled by the sync hooks above
    };

    initializeData();
  }, [contact?.id]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  
  const resolveId = (field) => {
    if (!field) return "";
    if (Array.isArray(field) && field.length > 0) return field[0]?.id || String(field[0]) || "";
    if (typeof field === "object") return field.id || "";
    return String(field);
  };

  const getInitialValues = () => {
    if (!contact) return { name: "", email: "", phone: "", organizationId: "", assignedToId: "" };
    
    return {
      name: contact.name || "",
      email: contact.email || "",
      phone: contact.phone || "",
      organizationId: resolveId(contact.organizationId),
      assignedToId: resolveId(contact.assignedToId),
    };
  };

  const validationSchema = Yup.object({
    name: Yup.string().min(2).max(100).required("Name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    phone: Yup.string().required("Phone is required"),
    organizationId: Yup.string().required("Organization is required"),
    assignedToId: Yup.string().required("Assigned To is required"),
  });



  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setIsLoading(true);
      const originalOrgId = resolveId(contact.organizationId);
      const newOrgId = values.organizationId;
      
      const payload = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        organizationId: newOrgId || "Not Available",
        removeFromOrganizationId: (originalOrgId !== newOrgId && originalOrgId) ? originalOrgId : undefined,
        assignedToId: typeof values.assignedToId === 'object' && values.assignedToId !== null
          ? values.assignedToId.id
          : values.assignedToId || "",
        updatedAt: new Date().toISOString(),
        updatedById: "current-user-id", // Should be from context
      };

      await updateContact(contact.id, payload);



      toast.success("Contact updated successfully!");
      
      // Notify parent to refresh list - this already handles the refresh
      if (onUpdateContact) {
        onUpdateContact(contact.id, payload);
      } else if (window.refreshContactsData) {
        // Only use global refresh if the local prop isn't handling it
        window.refreshContactsData();
      }
      
      if (window.refreshOrganizationsData) window.refreshOrganizationsData();
      if (onHide) onHide();
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data?.message || "Failed to update contact");
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  if (!contact) return null;

  return (
    <div className="p-4">
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue, isSubmitting: formikSubmitting, dirty }) => (
          <FormikForm>
            <ScrollToFieldError />
            <Row>
              <Col md={6}>
                <div className="mb-4">
                  <label className="form-label text-dark fw-bold">* Organization</label>
                  <CustomAsyncDropdown
                    isMulti={false}
                    options={orgsOptions}
                    value={orgsOptions.find(o => o.value === values.organizationId) || 
                      (Array.isArray(contact.organizationId) ? { value: contact.organizationId[0]?.id, label: contact.organizationId[0]?.name } : null)}
                    onChange={(val) => setFieldValue("organizationId", val?.value || "")}
                    onInputChange={(s) => {
                      setOrgsSearch(s);
                      fetchOrgs({ page: 1, search: s, append: false });
                    }}
                    onMenuOpen={() => {
                      if (!hasFetchedOrgs) {
                        fetchOrgs({ page: 1, search: orgsSearch, append: false });
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (orgsHasMore && !orgsLoading) {
                        fetchOrgs({ page: orgsPage + 1, search: orgsSearch, append: true });
                      }
                    }}
                    onRefresh={() => fetchOrgs({ page: 1, search: "", append: false })}
                    isLoading={orgsLoading}
                    placeholder="Search and select organization"
                  />
                  {touched.organizationId && errors.organizationId && (
                    <div className="text-danger small mt-1">{errors.organizationId}</div>
                  )}
                </div>

                <TextInput
                  label="Full Name"
                  name="name"
                  placeholder="Enter full name"
                  required
                />

                <TextInput
                  label="Email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  required
                />
              </Col>

              <Col md={6}>
                <TextInput
                  label="Phone"
                  name="phone"
                  placeholder="Enter phone number"
                  required
                />

                <div className="mb-4">
                  <label className="form-label text-dark fw-bold">* Assigned To</label>
                  <CustomAsyncDropdown
                    isMulti={false}
                    options={officersOptions}
                    value={officersOptions.find(o => o.value === values.assignedToId) || 
                      (() => {
                        const assigned = Array.isArray(contact.assignedToId) ? contact.assignedToId[0] : (typeof contact.assignedToId === 'object' ? contact.assignedToId : null);
                        if (!assigned) return null;
                        const name = assigned.firstName || assigned.lastName 
                          ? `${assigned.firstName || ""} ${assigned.lastName || ""}`.trim() 
                          : (assigned.name || "Unknown");
                        return { value: assigned.id, label: name };
                      })()}
                    onChange={(val) => setFieldValue("assignedToId", val?.value || "")}
                    onInputChange={(s) => {
                      setOfficersSearch(s);
                      fetchOfficers({ page: 1, search: s, append: false });
                    }}
                    onMenuOpen={() => {
                      if (!hasFetchedOfficers) {
                        fetchOfficers({ page: 1, search: officersSearch, append: false });
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (officersHasMore && !officersLoading) {
                        fetchOfficers({ page: officersPage + 1, search: officersSearch, append: true });
                      }
                    }}
                    onRefresh={() => fetchOfficers({ page: 1, search: "", append: false })}
                    isLoading={officersLoading}
                    placeholder="Search and select officer"
                  />
                  {touched.assignedToId && errors.assignedToId && (
                    <div className="text-danger small mt-1">{errors.assignedToId}</div>
                  )}
                </div>
              </Col>
            </Row>

            <Row className="mt-4">
              <Col md={12} className="d-flex justify-content-end gap-2">
                <Button variant="outline-secondary" onClick={onHide} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={!dirty || isLoading}
                  style={{ backgroundColor: "#ea580c", borderColor: "#ea580c" }}
                >
                  {isLoading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : "Update Contact"}
                </Button>
              </Col>
            </Row>
          </FormikForm>
        )}
      </Formik>
      <ToastContainer />
    </div>
  );
};

export default ContactDetailsTab;
